import { create } from "zustand";

import { api, HttpError } from "../api/rest";
import type { AccountCohort, AccountsResponse } from "../api/types";
import {
  cohortPreview,
  resolveTarget,
  type CohortPreview,
  type ResolvedTarget,
} from "../lib/accounts";
import { useSettings } from "./useSettings";

/**
 * Состав аккаунтов набора: опрос GET /api/v1/accounts.
 *
 * Опросом, а не по сокету: состав меняется по SIGHUP раз в дни, а web_api сам
 * перечитывает sidecar раз в 2 с — живой канал ради этого не нужен.
 *
 * Тикер уровня вкладки, как startPendingWatch: подписчиков много (выбор
 * аккаунта, столбцы ордеров и ленты, кнопки), а состав один.
 */
const POLL_MS = 5000;

interface AccountsState {
  /** Последний успешный ответ; null — ещё не было ни одного. */
  data: AccountsResponse | null;
  /** Текст последней ошибки опроса; прошлый состав при этом не стирается. */
  error: string | null;
  /** Когорты (T5.5); null — ещё не прочитаны или web_api их не отдал. */
  cohorts: AccountCohort[] | null;
  refresh: () => Promise<void>;
  /** Записать список когорт целиком и сразу показать записанное. */
  saveCohorts: (cohorts: Pick<AccountCohort, "name" | "accounts">[]) => Promise<void>;
}

export const useAccounts = create<AccountsState>((set) => ({
  data: null,
  error: null,
  cohorts: null,
  refresh: async () => {
    // Состав и когорты — порознь: база когорт может не отвечать, когда состав
    // известен, и наоборот, а одна ошибка не должна гасить другое знание.
    const [data, cohorts] = await Promise.allSettled([api.accounts(), api.accountCohorts()]);
    if (data.status === "fulfilled") {
      set({ data: data.value, error: null });
    } else {
      // Последний известный состав остаётся: мигание списка аккаунтов на
      // каждом сбое сети сбивало бы выбор оператора сильнее, чем устаревший
      // на секунды состав. Решает всё равно трейдер.
      const err: unknown = data.reason;
      set({ error: err instanceof HttpError ? err.message : String(err) });
    }
    if (cohorts.status === "fulfilled") set({ cohorts: cohorts.value.cohorts });
  },
  saveCohorts: async (cohorts) => {
    const res = await api.setAccountCohorts(cohorts);
    set({ cohorts: res.cohorts });
  },
}));

let pollTimer: ReturnType<typeof setInterval> | null = null;

export function startAccountsWatch(): void {
  if (pollTimer !== null) return;
  void useAccounts.getState().refresh();
  pollTimer = setInterval(() => void useAccounts.getState().refresh(), POLL_MS);
}

/**
 * Активная строка быстрых действий — выбор оператора, проверенный по составу.
 *
 * От её имени бьют горячие клавиши, берутся сигналы, считается тир и
 * закрывается пробная позиция. У КНОПОК быстрых действий цель своя — строка,
 * в которой нажали, — а у формы ордера своя (useFormTarget): с 2026-10-07 одной
 * общей цели на всю панель больше нет.
 */
export function useCommandTarget(): ResolvedTarget {
  const choice = useSettings((s) => s.settings.target);
  const data = useAccounts((s) => s.data);
  const cohorts = useAccounts((s) => s.cohorts);
  return resolveTarget(choice, data, cohorts);
}

/**
 * Есть ли годная цель — одним булевым. Для кнопок, которых на панели десятки:
 * полный useCommandTarget перерисовывал бы каждую на каждом опросе состава
 * (раз в 5 с), а булево меняется только вместе с выбором или составом.
 */
export function useHasCommandTarget(): boolean {
  const choice = useSettings((s) => s.settings.target);
  return useAccounts((s) => resolveTarget(choice, s.data, s.cohorts).target !== null);
}

/**
 * Та же цель, но в момент нажатия, без подписки: для обработчиков и горячих
 * клавиш. Значение, захваченное замыканием при рендере, могло устареть, пока
 * оператор вёл мышь, — а нажатие обязано уйти от имени того, что выбрано
 * СЕЙЧАС.
 */
export function commandTargetNow(): ResolvedTarget {
  const { data, cohorts } = useAccounts.getState();
  return resolveTarget(useSettings.getState().settings.target, data, cohorts);
}

/**
 * Цель формы «Новый ордер»: свой выбор, проверенный по составу тем же
 * resolveTarget. Действует только на форму.
 */
export function useFormTarget(): ResolvedTarget {
  const choice = useSettings((s) => s.settings.formTarget);
  const data = useAccounts((s) => s.data);
  const cohorts = useAccounts((s) => s.cohorts);
  return resolveTarget(choice, data, cohorts);
}

/**
 * Предпросмотр команды формы когортой: «3 аккаунта × $25» до нажатия; null —
 * выбран аккаунт или ничего. Сумма — та, что поставит КАЖДЫЙ аккаунт (Д-5).
 */
export function useFormCohortPreview(usd: string | null | undefined): CohortPreview | null {
  const choice = useSettings((s) => s.settings.formTarget);
  const data = useAccounts((s) => s.data);
  const cohorts = useAccounts((s) => s.cohorts);
  if (choice?.kind !== "cohort") return null;
  const cohort = cohorts?.find((c) => c.name === choice.name);
  return cohort ? cohortPreview(cohort, data, usd) : null;
}
